import React, { FC, useMemo } from 'react';
import { Heading, Icon, Image, Link, Text, Types } from '@marriott/mi-ui-library';

import { WalletAwardTile } from '../../../../molecules';
import { WalletFreeNightAwardsProps } from '../../types';
import { buildWalletAwardExpirationText } from '../../utils/buildWalletAwardExpirationText';
import { StyledWalletFreeNightAwardsSection } from './WalletFreeNightAwardsSection.styles';

type FreeNightAwardCardViewModel = {
  id: string;
  eyebrow?: string;
  title: string;
  expirationText?: string;
  statusLabel: string;
  statusVariant: 'success' | 'neutral' | 'info';
  statusIconClass?: string;
  mediaImageSrc?: string;
  mediaImageAltText?: string;
};

/**
 * Free Night Awards section (freeNight slice). Presentational only; receives the
 * already-fetched FNA items, the shared `common` slice, and the per-type empty
 * state content from the WalletAwardsSection orchestrator.
 */
export const WalletFreeNightAwardsSection: FC<WalletFreeNightAwardsProps> = ({
  common,
  freeNight,
  emptyState,
  items,
  memberIconColor,
  currentLocale,
  onHowToEarnClick,
  onAwardTileClick,
}) => {
  const cards = useMemo<FreeNightAwardCardViewModel[]>(() => {
    return items.map(item => {
      const expirationText = buildWalletAwardExpirationText(common?.expiresLabel, item.expirationDate, currentLocale);
      const awardTypeTitle = item.awardDescription || '';
      const isApplied = item.status === 'applied';
      const statusLabel = isApplied ? common?.appliedLabel || 'Applied' : common?.availableLabel || 'Available';
      const statusVariant: 'success' | 'neutral' | 'info' = isApplied ? 'neutral' : 'success';
      const statusIconClass = isApplied ? common?.appliedStatusIcon || 'icon-checkmark-circle' : undefined;

      return {
        id: item.id,
        eyebrow: isApplied ? awardTypeTitle : undefined,
        title: isApplied ? item.propertyName || awardTypeTitle : awardTypeTitle,
        expirationText: isApplied ? undefined : expirationText,
        statusLabel,
        statusVariant,
        statusIconClass,
        mediaImageSrc: isApplied ? item.propertyImageSrc : undefined,
        mediaImageAltText: isApplied ? item.propertyImageAltText : undefined,
      };
    });
  }, [
    items,
    common?.availableLabel,
    common?.appliedLabel,
    common?.appliedStatusIcon,
    common?.expiresLabel,
    currentLocale,
  ]);

  const totalCount = items.length;
  const emptyCtaLabel = emptyState?.freeNightEmptyLinkLabel || freeNight?.descriptionLinkLabel;
  const emptyCtaLink = emptyState?.freeNightEmptyLink || freeNight?.descriptionLink;
  const emptyCtaOpenInNewTab = emptyState?.freeNightEmptyLinkOpenInANewTab ?? freeNight?.descriptionLinkOpenInANewTab;
  const emptyImage = emptyState?.freeNightEmptyDynamicImage;
  const emptyImageUrl = emptyImage?.assetPath || emptyState?.freeNightEmptyImage;
  const emptyImageAlt =
    emptyImage?.altText || emptyState?.freeNightEmptyImageAltText || emptyState?.freeNightEmptyAltText || '';

  return (
    <StyledWalletFreeNightAwardsSection
      data-testid="wallet-free-night-awards-section"
      data-component-name="o-account-WalletFreeNightAwardsSection"
    >
      {items.length > 0 ? (
        <div className="wallet-free-night-awards-section__heading-row">
          <Heading
            variation={Types.headingType.subtitle}
            fontSize={Types.size.large}
            titleText={freeNight?.sectionHeading || ''}
            customClass="wallet-free-night-awards-section__heading m-0"
          />
          {totalCount > 0 ? <span className="wallet-free-night-awards-section__count">{totalCount}</span> : null}
        </div>
      ) : null}

      {cards.length > 0 &&
      (freeNight?.description ||
        (freeNight?.descriptionLinkLabel && (freeNight?.descriptionLink || onHowToEarnClick))) ? (
        <p className="wallet-free-night-awards-section__description t-font-m m-0">
          {freeNight?.description ? <span>{freeNight.description} </span> : null}
          {freeNight?.descriptionLinkLabel && (freeNight?.descriptionLink || onHowToEarnClick) ? (
            onHowToEarnClick ? (
              <Link
                linkClassName="wallet-free-night-awards-section__description-link ml-1"
                text={freeNight.descriptionLinkLabel}
                linkHref="#"
                callback={(event?: React.MouseEvent) => {
                  event?.preventDefault();
                  onHowToEarnClick();
                }}
              />
            ) : (
              <Link
                linkClassName="wallet-free-night-awards-section__description-link ml-1"
                target={freeNight.descriptionLinkOpenInANewTab ? '_blank' : '_self'}
                rel={freeNight.descriptionLinkOpenInANewTab ? 'noopener noreferrer' : ''}
                text={freeNight.descriptionLinkLabel}
                linkHref={freeNight.descriptionLink}
              />
            )
          ) : null}
        </p>
      ) : null}

      {cards.length > 0 ? (
        <div className="wallet-free-night-awards-section__tiles">
          {cards.map((card, index) => (
            <WalletAwardTile
              key={card.id}
              eyebrow={card.eyebrow}
              title={card.title}
              expirationText={card.expirationText}
              statusLabel={card.statusLabel}
              statusVariant={card.statusVariant}
              statusIconClass={card.statusIconClass}
              mediaIconClass={card.mediaImageSrc ? undefined : freeNight?.freeNightAwardIcon}
              mediaIconColor={card.mediaImageSrc ? undefined : memberIconColor}
              mediaImageSrc={card.mediaImageSrc}
              mediaImageAltText={card.mediaImageAltText}
              ariaLabel={card.title}
              testId="wallet-free-night-award-tile"
              onClick={onAwardTileClick ? () => onAwardTileClick(items[index]) : undefined}
            />
          ))}
        </div>
      ) : emptyState?.freeNightEmptyMessage ? (
        <div className="wallet-free-night-awards-section__empty-state">
          <div className="wallet-free-night-awards-section__empty-media" aria-hidden="true">
            {emptyImageUrl ? (
              <Image
                customClass="wallet-free-night-awards-section__empty-image"
                altText={emptyImageAlt}
                defaultImageURL={emptyImageUrl}
                renditions={emptyImage?.renditions as never}
                dynamic={emptyImage?.dynamic}
              />
            ) : freeNight?.freeNightAwardIcon ? (
              <Icon iconClass={freeNight.freeNightAwardIcon} isDecorative ariaLabel="" />
            ) : null}
          </div>
          <div className="wallet-free-night-awards-section__empty-text">
            {emptyState?.freeNightEmptyHeading ? (
              <Text
                element={Types.tags.div}
                fontSize={Types.size.small}
                copyText={emptyState.freeNightEmptyHeading}
                customClass="wallet-free-night-awards-section__empty-heading t-font-weight-m"
              />
            ) : null}
            <Text
              element={Types.tags.div}
              fontSize={Types.size.small}
              copyText={emptyState.freeNightEmptyMessage}
              customClass="wallet-free-night-awards-section__empty-message m-0"
            />
          </div>
          {emptyCtaLabel && (emptyCtaLink || onHowToEarnClick) ? (
            <div className="wallet-free-night-awards-section__empty-cta ml-3">
              {onHowToEarnClick ? (
                <Link
                  linkClassName="wallet-free-night-awards-section__empty-cta-link"
                  text={emptyCtaLabel}
                  ariaLabel={emptyCtaLabel}
                  linkHref="#"
                  callback={(event?: React.MouseEvent) => {
                    event?.preventDefault();
                    onHowToEarnClick();
                  }}
                />
              ) : (
                <Link
                  linkClassName="wallet-free-night-awards-section__empty-cta-link"
                  text={emptyCtaLabel}
                  ariaLabel={emptyCtaLabel}
                  linkHref={emptyCtaLink}
                  target={emptyCtaOpenInNewTab ? '_blank' : '_self'}
                />
              )}
              <Icon iconClass="icon-forward-arrow" isDecorative ariaLabel="" />
            </div>
          ) : null}
        </div>
      ) : null}
    </StyledWalletFreeNightAwardsSection>
  );
};
